"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styles from "./SignUp.module.css";
import { FaEnvelope, FaLock, FaExclamationCircle, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaGraduationCap, FaUpload, FaPhone, FaTimes } from "react-icons/fa";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GuestNav from './Navbars/GuestNav';
import Footer from './Footer';
import countryList from "react-select-country-list";
import axios from 'axios';

// Import images
import pencil from "../assets/pencil.png";
import cross from "../assets/cross.png";
import login1 from "../assets/login1.png";
import login2 from "../assets/login2.png";

const SignUp = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    parentName: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: null,
    location: "",
    country: null,
    dateOfBirth: null,
    whatsappNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [fileError, setFileError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dateOfBirth: date,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const fileSize = file.size / 1024; // Convert bytes to KB
      const allowedExtensions = ['image/png', 'image/jpeg', 'image/jpg'];

      if (!allowedExtensions.includes(fileType)) {
        setAvatar(null);
        setAvatarPreview(null);
        setFileError('Please upload a .png, .jpeg, or .jpg file.');
      } else if (fileSize > 5120) {
        setAvatar(null);
        setAvatarPreview(null);
        setFileError('File size should not exceed 5MB.');
      } else {
        setAvatar(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setFileError('');
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First Name must be at least 2 characters";
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = "First Name must be less than 50 characters";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last Name must be at least 2 characters";
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = "Last Name must be less than 50 characters";
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.trim().length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    } else if (formData.username.trim().length > 30) {
      newErrors.username = "Username must be less than 30 characters";
    }

    // Parent Name validation
    if (!formData.parentName.trim()) {
      newErrors.parentName = "Parent Name is required";
    } else if (formData.parentName.trim().length < 2) {
      newErrors.parentName = "Parent Name must be at least 2 characters";
    } else if (formData.parentName.trim().length > 100) {
      newErrors.parentName = "Parent Name must be less than 100 characters";
    }

    // WhatsApp Number validation
    if (!formData.whatsappNumber.trim()) {
      newErrors.whatsappNumber = "WhatsApp Number is required";
    } else if (!/^\+?[0-9]{10,15}$/.test(formData.whatsappNumber.trim())) {
      newErrors.whatsappNumber = "Please enter a valid WhatsApp number (10-15 digits)";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm Password is required";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Gender validation
    if (!formData.gender) newErrors.gender = "Gender is required";

    // Location validation
    if (!formData.location.trim()) {
      newErrors.location = "Address is required";
    } else if (formData.location.trim().length < 10) {
      newErrors.location = "Address must be at least 10 characters";
    }

    // Country validation
    if (!formData.country) newErrors.country = "Country is required";

    // Date of Birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of Birth is required";
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (isNaN(age) || age < 3 || age > 100) {
        newErrors.dateOfBirth = "Please enter a valid date of birth (age 3-100)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!avatar) {
      toast.error('Image is required', { position: 'top-center' });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Password does not match with confirm password.', { position: 'top-center' });
      return;
    }
    if (fileError) {
      toast.error(fileError, { position: 'top-center' });
      return;
    }
    setLoading(true);
    try {
      // Check if username is unique
      const uniqueCheck = await axios.post(
        `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/checkUniqueUsername`,
        { username: formData.username.trim() }
      );
      if (!uniqueCheck.data.isUnique) {
        toast.error('Username already exists. Please choose a different username.', { position: 'top-center' });
        setLoading(false);
        return;
      }
      // Create FormData object
      const registrationData = new FormData();
      registrationData.append("selectedFile", avatar);
      registrationData.append("signupUsername", formData.username.trim());
      registrationData.append("usertype", 'Student');
      registrationData.append("signupEmail", formData.email.trim());
      registrationData.append("signupPassword", formData.password);
      registrationData.append("confirmPassword", formData.confirmPassword);
      registrationData.append("firstName", formData.firstName.trim());
      registrationData.append("lastName", formData.lastName.trim());
      registrationData.append("gender", formData.gender ? (formData.gender.label || formData.gender) : "");
      registrationData.append("dateOfBirth", formData.dateOfBirth ? formData.dateOfBirth : "");
      registrationData.append("parentName", formData.parentName.trim());
      registrationData.append("whatsappNumber", formData.whatsappNumber.trim());
      registrationData.append("address", formData.location.trim());
      registrationData.append("schoolCode", "");
      registrationData.append("deactivated", 0);
      registrationData.append("selectedCountry", formData.country ? (formData.country.label || formData.country) : "");
      // Register user
      const registerResponse = await axios.post(
        `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/register`,
        registrationData
      );
      const insertId = registerResponse.data.insertId;
      if (!insertId) {
        throw new Error("Registration failed: No user ID returned.");
      }
      // Register in student table
      await axios.post(
        `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/registerInStudentTable`,
        { user_id: insertId }
      );
      toast.success('Successfully Signed Up! Redirecting to Sign In...', { position: 'top-center', autoClose: 2000 });

      // Redirect to the sign-in page after a delay
      setTimeout(() => {
        navigate('/sign-in');
      }, 2000);

    } catch (err) {
      console.error("Signup error:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Signup Unsuccessful!';
      toast.error(errorMessage, { position: 'top-center', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Gender options
  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
  ];

  // Country options using react-select-country-list
  const countryOptions = useMemo(() => countryList().getData(), []);

  return (
    <>
      {/* <GuestNav /> */}
      <div className={styles.signupContainer}>
        <ToastContainer position="top-center" autoClose={3000} pauseOnFocusLoss={false} draggable pauseOnHover={false} />
        <div className={styles.signupBox}>
          <img src={login1} alt="Login decoration 1" className={styles.login1} />
          <img src={login2} alt="Login decoration 2" className={styles.login2} />
          <FaTimes className={styles.crossIcon} onClick={() => navigate("/sign-in")} />

          <div className={styles.headerSection}>
            <img src={pencil} alt="Pencil" className={styles.pencilIcon} />
            <h2 className={styles.signupHeading}>Signup to Polymath Kids</h2>
          </div>

          <div className={styles.avatarUploadContainer}>
            <label htmlFor="avatar-upload" className={styles.avatarCircle}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar Preview" className={styles.avatarPreview} />
              ) : (
                <FaUpload className={styles.uploadIcon} />
              )}
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: "none" }}
              />
            </label>
            <span className={styles.uploadText}>Upload Avatar</span>
            {fileError && <span className={styles.fileErrorMessage}>{fileError}</span>}
          </div>

          <form onSubmit={handleSubmit} className={styles.signupForm}>
            <div className={styles.formGrid}>
              <div className={styles.columnLeftColumn}>
                <div className={styles.formGroupFirstNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.firstName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.firstName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupUsernameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      placeholder="Username"
                      value={formData.username}
                      onChange={handleChange}
                      className={errors.username ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.username && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.username}
                    </span>
                  )}
                </div>



                <div className={styles.formGroupGenderField}>
                  <div className={styles.inputWrapper}>
                    <Select
                      id="gender"
                      name="gender"
                      placeholder="Select Gender"
                      value={formData.gender}
                      onChange={(selectedOption) => handleSelectChange("gender", selectedOption)}
                      options={genderOptions}
                      classNamePrefix="react-select"
                      className={errors.gender ? `${styles.errorSelect}` : ""}
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px', // Corrected from 'font-size' to 'fontSize'
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.gender && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.gender}
                    </span>
                  )}
                </div>
                <div className={styles.formGroupPasswordField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      className={errors.password ? `${styles.error}` : ""}
                    />
                    <FaLock className={styles.inputIcon} />
                  </div>
                  {errors.password && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.password}
                    </span>
                  )}
                </div>
                <div className={styles.formGroupLocationField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      placeholder="Address"
                      value={formData.location}
                      onChange={handleChange}
                      className={errors.location ? `${styles.error}` : ""}
                    />
                    <FaMapMarkerAlt className={styles.inputIcon} />
                  </div>
                  {errors.location && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.location}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupCountryField}>
                  <div className={styles.inputWrapper}>
                    <Select
                      id="country"
                      name="country"
                      placeholder="Choose Country"
                      value={formData.country}
                      onChange={(selectedOption) => handleSelectChange("country", selectedOption)}
                      options={countryOptions}
                      classNamePrefix="react-select"
                      className={errors.country ? `${styles.errorSelect}` : ""}
                      isSearchable={true}
                      isClearable={true}
                      menuPlacement="auto"
                      styles={{
                        control: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          border: 'none !important',
                          boxShadow: 'none !important',
                          minHeight: '58px',
                          borderRadius: '18px',
                          padding: '0 17px',
                          '&:hover': {
                            borderColor: '#DCDCDC'
                          }
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          padding: '0 !important',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#888 !important',
                        }),
                        input: (base) => ({
                          ...base,
                          background: 'transparent !important',
                          color: '#333 !important',
                          padding: '0 !important',
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: 'none !important',
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: '#888 !important',
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          zIndex: '100',
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '10px 17px',
                          fontSize: '16px', // Corrected from 'font-size' to 'fontSize'
                          color: '#333',
                          backgroundColor: state.isSelected ? '#812470' : state.isFocused ? '#e0e0e0' : 'white',
                          '&:active': {
                            backgroundColor: '#812470',
                          },
                        }),
                      }}
                    />
                  </div>
                  {errors.country && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.country}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.columnRightColumn}>
                <div className={styles.formGroupLastNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.lastName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.lastName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupParentNameField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      placeholder="Parent Name"
                      value={formData.parentName}
                      onChange={handleChange}
                      className={errors.parentName ? `${styles.error}` : ""}
                    />
                    <FaUser className={styles.inputIcon} />
                  </div>
                  {errors.parentName && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.parentName}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupEmailField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? `${styles.error}` : ""}
                    />
                    <FaEnvelope className={styles.inputIcon} />
                  </div>
                  {errors.email && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.email}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupConfirmPasswordField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={errors.confirmPassword ? `${styles.error}` : ""}
                    />
                    <FaLock className={styles.inputIcon} />
                  </div>
                  {errors.confirmPassword && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.confirmPassword}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupDobField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth || ''}
                      onChange={handleChange}
                      className={errors.dateOfBirth ? `${styles.error}` : ""}
                      style={{
                        colorScheme: 'light',
                        position: 'relative',
                        color: formData.dateOfBirth ? '#000' : 'transparent',
                        WebkitTextFillColor: formData.dateOfBirth ? '#000' : 'transparent',
                        // WebkitOpacity: formData.dateOfBirth ? 1 : 0
                      }}
                    />
                    {!formData.dateOfBirth && (
                      <label
                        htmlFor="dateOfBirth"
                        style={{
                          position: 'absolute',
                          left: '17px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#888',
                          pointerEvents: 'none',
                          fontSize: '12px',
                          zIndex: 1,
                          background: 'transparent'
                        }}
                      >
                        Date of Birth
                      </label>
                    )}
                  </div>
                  {errors.dateOfBirth && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.dateOfBirth}
                    </span>
                  )}
                </div>

                <div className={styles.formGroupSchoolCodeField}>
                  <div className={styles.inputWrapper}>
                    <input
                      type="tel"
                      id="whatsappNumber"
                      name="whatsappNumber"
                      placeholder="WhatsApp Number"
                      value={formData.whatsappNumber}
                      onChange={handleChange}
                      className={errors.whatsappNumber ? `${styles.error}` : ""}
                    />
                    <FaPhone className={styles.inputIcon} />
                  </div>
                  {errors.whatsappNumber && (
                    <span className={styles.errorMessage}>
                      <FaExclamationCircle /> {errors.whatsappNumber}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className={styles.signupButton} disabled={loading}>
              {loading ? "Signing Up..." : "Sign Up"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUp;